// FEAT-023 — 릴리즈 표시 규칙. 전사 화면(/releases)과 제품 상세 「릴리즈」 탭이 공유한다.
//
// 같은 릴리즈가 두 화면에서 다르게 읽히면 안 되므로 판정은 여기 한 곳에만 둔다.
// (프로젝트 진행률에서 projectProgress.js 가 맡는 자리와 같은 역할)

const DAY_MS = 86400000;

/** ISO 타임스탬프 → KST 기준 YYYY-MM-DD. GitHub 은 UTC 를 주므로 옮겨서 쓴다. */
export function toKstDate(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

/** 오늘 (KST, YYYY-MM-DD). */
export function todayKst() {
  return new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

/** a → b 일수. 둘 다 YYYY-MM-DD. */
export function daysBetween(a, b) {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);
}

/** 'YYYY-MM-DD' → '9월 16일' (연도가 올해와 다르면 연도까지). */
export function formatDate(ymd, refYear) {
  if (!ymd) return '미정';
  const [y, m, d] = ymd.split('-');
  const year = refYear ?? todayKst().slice(0, 4);
  const md = `${Number(m)}월 ${Number(d)}일`;
  return y === year ? md : `${y}년 ${md}`;
}

/**
 * 제품 버전(마일스톤) = 계획 축.
 * 이력(릴리즈)과 입자가 다르다 — 섞지 않고 각각의 단위로 보여준다.
 * 목표일이 지났는데 열려 있으면 경과 일수를 함께 돌려준다.
 */
export function readPlan(product) {
  const ms = product?.milestone;
  if (!ms?.tag) return null;
  const due = ms.due_on ? ms.due_on.slice(0, 10) : null;
  const open = ms.state !== 'closed';
  const overdueDays = due && open ? daysBetween(due, todayKst()) : 0;
  return {
    tag: ms.tag,
    due,
    open,
    state: open ? '진행 중' : '마감',
    overdue: overdueDays > 0 ? overdueDays : 0,
  };
}

/** 사전 배포 여부 라벨. '정식' 과 구분해 두어야 오해가 없다. */
export const releaseKind = (r) => (r.prerelease ? '사전 배포' : '정식');

/**
 * 제품 id → 색. 달력에서 제품을 구분하는 용도라 대비만 확보하면 된다.
 * 고정 팔레트를 id 해시로 배정해 새 제품이 생겨도 색이 밀리지 않는다.
 */
const PALETTE = [
  '#3fb950', '#58a6ff', '#d29922', '#a371f7', '#f778ba',
  '#ff7b72', '#56d4dd', '#e3b341', '#7ee787', '#bc8cff',
];
export function productColor(id) {
  if (!id) return '#8b949e'; // 제품 미지정
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/** 릴리즈 목록 → 'YYYY-MM-DD' → 릴리즈[] (달력 배치용). */
export function groupByDate(releases) {
  const map = new Map();
  for (const r of releases) {
    const d = toKstDate(r.published);
    if (!d) continue;
    if (!map.has(d)) map.set(d, []);
    map.get(d).push(r);
  }
  return map;
}

/** 해당 월(YYYY-MM)의 달력 격자 — 앞뒤 빈 칸 포함, 일요일 시작. */
export function buildMonthGrid(ym) {
  const [y, m] = ym.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = first.getUTCDay();
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** 'YYYY-MM' 더하기/빼기. */
export function shiftMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
